<script setup>
import { apiQuery } from "@/api/j24api";
import { ref, defineProps, defineEmits, onMounted, computed, watch } from "vue";
import BasicTable from "./BasicTableV2.vue";
import PatchLogs from "./PatchLogs.vue";
import InventoryAppsList from "./InventoryAppsList.vue";
import PatchesAvailable from "./PatchesAvailable.vue";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();
import { patch24Store } from "@/stores/patch24";
import { storeToRefs } from "pinia";
const patch24 = patch24Store();
const { selectedLicense } = storeToRefs(patch24);

const props = defineProps({
  device: Object,
  mdmServerId: String,
  licenseId: String,
});

let extendedInfo = ref({});
let loading = ref(false);
let selectedTab = ref("Overview");
let device = ref({}); // Used for device data fetched from API
let patchCommands = ref([]);

watch(
  () => props.device,
  async (newValue) => {
    await getData(newValue);
  },
  { immediate: true }
);

const disableDelete = computed(() => {
  let result =
    permissions.$state &&
    permissions.$state["PatchingV2-Devices"] &&
    permissions.$state["PatchingV2-Devices"].actions.includes("Delete");
  return !result;
});

async function getData(newValue) {
  device.value = {
    updatesAvailable: [],
  };
  loading.value = true;
  console.log("DeviceView watch", newValue);
  try {
    if (props.device) {
      let result = await apiQuery("ELA", "patching/patchingManagement", {
        action: "getDevices",
        id: props.licenseId,
        deviceID: props.device._id,
        appInventory: true,
      });
      if (result.length) {
        device.value = result[0];
      }
      let rules = await apiQuery("ELA", "patching/patchingManagement", {
        action: "getDeviceCommands",
        serial: props.device.serial,
        licenseId: props.licenseId,
      });
      patchCommands.value.length = 0;
      if (rules.result.length) {
        patchCommands.value.push(...rules.result);
      }
      // Now look for global commands
      patchCommands.value.push(
        ...selectedLicense.value.GlobalConfiguration?.Commands?.filter(
          (cmd) => cmd.status != "cancelled"
        )
      ) || [];
      // And configuration commands
      if (props.device.configurationId) {
        let configCommands = selectedLicense.value.Configurations.find(
          (config) => config.ConfigurationID == props.device.configurationId
        )?.Commands;
        if (configCommands?.length) {
          patchCommands.value.push(
            ...configCommands.filter((cmd) => cmd.status != "cancelled")
          );
        }
      }
    }

    if (props.mdmServerId) {
      let result = await apiQuery("ELA", "cmd24/query", {
        dataRequested: "computerDetail",
        id: props.device.id,
        maxage: 86400,
        mdmServerId: props.mdmServerId,
      });
      if (result.result) {
        extendedInfo.value = result.result;
      }
    }
  } catch (error) {
    console.log("Error getting device info", error);
  }
  loading.value = false;
}

const updatesAvailableCount = computed(() => {
  return (
    device.value.updatesAvailable?.filter(
      (update) => update.status === "ReadyToInstall"
    ).length || 0
  );
});

const topIconRow = computed(() => {
  let result = [];
  var smartStatus = null;
  if (device.value.stats?.HardwareStats?.smartStatus) {
    smartStatus =
      device.value.stats.HardwareStats.smartStatus == "Verified"
        ? "Verified"
        : "Failed";
  }
  var macOSVersion;
  if (device.value.macOSVersion) {
    let match = device.value.macOSVersion.match(/Version\s([\d.]+)/i);
    macOSVersion = match ? match[1] : "N/A";
  }

  if (device.value) {
    result.push({
      title: "Encrypted",
      icon: "mdi-lock",
      disabled:
        device.value.stats?.HardwareStats.filevaultEnabled !== undefined
          ? false
          : true,
      tooltip: "Device encryption Status",
      disabledTooptip: "Unable to determine encryption status",
      value:
        device.value.stats?.HardwareStats?.filevaultEnabled == true
          ? "Yes"
          : device.value.stats?.HardwareStats.filevaultEnabled !== undefined
          ? "No"
          : "N/A",
    });
    if (props.mdmServerId) {
      result.push({
        title: "Recovery Key",
        icon: "mdi-key",
        disabled: extendedInfo.value.diskEncryption
          ?.individualRecoveryKeyValidityStatus
          ? false
          : true,
        tooltip: "Device Recovery Key",
        disabledTooptip: "Unable to determine recovery key status",
        value:
          extendedInfo.value.diskEncryption
            ?.individualRecoveryKeyValidityStatus == "VALID"
            ? "Yes"
            : "No",
      });
    }
    result.push(
      {
        title: "Storage",
        icon: "mdi-folder-outline",
        disabled: device.value.stats?.HardwareStats?.freeDiskSpaceGB
          ? false
          : true,
        tooltip: "Storage space available on boot drive",
        disabledTooptip: "Unable to determine storage space",
        value: device.value.stats?.HardwareStats?.freeDiskSpaceGB
          ? device.value.stats?.HardwareStats?.freeDiskSpaceGB + " GB"
          : "N/A",
      },
      {
        title: "S.M.A.R.T Status",
        icon: "mdi-content-save",
        disabled: smartStatus ? false : true,
        tooltip: "Disk SMART status",
        disabledTooptip: "Unable to determine disk SMART status",
        value: smartStatus == "Verified" ? "Good" : "Failed",
      },
      {
        title: "OS Version",
        icon: "mdi-cellphone-link",
        disabled: device.value.macOSVersion ? false : true,
        tooltip: "Operating System Version",
        disabledTooptip: "Unable to determine OS version",
        value: macOSVersion,
      }
    );
  }
  return result;
});

const general = computed(() => {
  let result = [];
  if (props.device) {
    result.push(
      {
        name: "Serial Number",
        value: device.value.serial,
      },
      {
        name: "Model",
        value: device.value.model,
      },
      {
        name: "OS Version",
        value: device.value.macOSVersion,
      },
      {
        name: "Username",
        value: device.value.username,
      },
      {
        name: "Full Name",
        value: device.value.fullName,
      }
    );
    if (props.mdmServerId) {
      result.push({
        name: "Last MDM Checkin",
        value: props.device.Last_Check_in,
      });
    }
    result.push({
      name: "Last Contact",
      value: props.device.patchLastContact
        ? new Date(props.device.patchLastContact).toLocaleString()
        : "N/A",
    });
  }
  return {
    sectionTitle: "General",
    fields: result,
    expanded: true,
  };
});

const hardware = computed(() => {
  let result = [];
  let ram = extendedInfo.value.hardware?.totalRamMegabytes;
  if (ram) {
    ram = (ram / 1024).toFixed(0) + " GB";
  } else {
    ram = "N/A";
  }
  var diskSize = "N/A";
  let disk = extendedInfo.value.storage?.disks;
  if (disk?.length) {
    diskSize = disk[0].sizeMegabytes;
    if (diskSize) {
      diskSize = (diskSize / 1024).toFixed(0) + " GB";
    } else {
      diskSize = "N/A";
    }
  }
  if (props.device) {
    result.push(
      {
        name: "Capacity",
        value: device.value.stats?.HardwareStats?.totalDiskSpaceGB
          ? device.value.stats.HardwareStats.totalDiskSpaceGB + " GB"
          : "N/A",
      },

      {
        name: "Processor Type",
        value: device.value.stats?.HardwareStats?.chip_type
          ? device.value.stats.HardwareStats.chip_type
          : "N/A",
      },
      {
        name: "Memory Size",
        value: device.value.stats?.HardwareStats?.totalMemoryGB
          ? device.value.stats.HardwareStats.totalMemoryGB + " GB"
          : "N/A",
      },
      {
        name: "MAC Address",
        value: getMacAddress(),
      },
      {
        name: "UDID",
        value: device.value.stats?.HardwareStats?.deviceUDID
          ? device.value.stats.HardwareStats.deviceUDID
          : "N/A",
      }
    );
  }
  return {
    sectionTitle: "Hardware",
    fields: result,
    expanded: true,
  };
});

function getMacAddress() {
  // find en0 interface
  if (device.value.stats?.HardwareStats?.networkInterfaces) {
    let en0 = device.value.stats.HardwareStats.networkInterfaces.find(
      (iface) => iface.interface === "en0"
    );
    if (en0) {
      return en0.macAddress || "N/A";
    }
  }
  return "N/A";
}

const emit = defineEmits(["back", "backRefresh"]);

onMounted(() => {
  console.log("DeviceView mounted");
  console.log(props.device);
});

function isoToDateOnly(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  // Returns date in YYYY-MM-DD format
  return date.toISOString().split("T")[0];
}

//MARK: Delete Device
const deleteDeviceDialog = ref(false);
const deleteDeviceLoading = ref(false);

async function deleteDeviceConfirmed() {
  deleteDeviceLoading.value = true;
  try {
    await apiQuery("ELA", "patching/patchingManagement", {
      action: "deleteDevice",
      deviceID: device.value._id,
      licenseId: props.licenseId,
    });
    console.log("Device deleted successfully");
    deleteDeviceDialog.value = false;
    emit("backRefresh"); // Navigate back to device list
  } catch (error) {
    console.error("Error deleting device:", error);
  }
  deleteDeviceLoading.value = false;
}

//MARK: PatchAction
const patchActionOverlay = ref(false);
const selectedPatch = ref({});
const patchActionTypes = ref([
  { title: "Default", value: "default" },
  { title: "Install Soon", value: "installSoon" },
  { title: "Install ASAP", value: "installASAP" },
]); // Used in template for v-select
const patchActionDetail = ref([]);
const patchCommandLoading = ref(false);

async function patchActionPressed(patch) {
  console.log("Patch Action Pressed", patch);
  selectedPatch.value = patch;
  selectedPatch.value.type = "default"; // Default type
  patchActionOverlay.value = true;
  var versionIdentifier = "CFBundleShortVersionString";
  try {
    patch.definition.latestApprovedPath.components.forEach((component) => {
      if (component.criteria) {
        component.criteria.forEach((criteria) => {
          if (criteria.name == "Application Version") {
            if (criteria.plistKey) {
              versionIdentifier = criteria.plistKey;
            }
          }
        });
      }
    });
  } catch (error) {
    console.error("Invalid Patch Structure", error);
  }

  patchActionDetail.value.length = 0; // Clear previous details
  patchActionDetail.value.push(
    ...[
      {
        title: "Patch Name",
        value: patch.definition.name,
      },
      {
        title: "Version",
        value: patch.version,
      },
      {
        title: "Release Date",
        value: isoToDateOnly(patch.definition.latestApprovedPatch.approvedAt),
      },
      {
        title: "Installed App Location",
        value: patch.inventory.path,
      },
      {
        title: "Installed App Version",
        value: patch.inventory[versionIdentifier] || "N/A",
      },
    ]
  );
}

async function patchActionSaveButton() {
  console.log("Patch Action Save Button Pressed", selectedPatch.value);
  patchCommandLoading.value = true;
  if (selectedPatch.value.type == "default") {
    console.log("Default action selected, no action taken");
  } else {
    try {
      let result = await apiQuery("ELA", "patching/patchingManagement", {
        action: "addDeviceCommand",
        licenseId: props.licenseId,
        serial: device.value.serial,
        type: selectedPatch.value.type,
        appId: selectedPatch.value.definition.id,
        version: selectedPatch.value.definition.latestApprovedPatch.version,
        status: "pending",
      });
      getData(props.device); // Refresh device data
    } catch (error) {
      console.error("Error applying patch action", error);
    }
  }
  patchCommandLoading.value = false;
  patchActionOverlay.value = false;
  selectedPatch.value = {};
}

function doesPatchCommandExist(patch) {
  console.log("Checking if patch command exists for", patch);

  let found = patchCommands.value.find(
    (command) =>
      command.appId === patch.definition.id && command.version === patch.version
  );
  if (!found) {
    return false;
  }
  var displayTxt = "Unknown";
  if (found.type == "installSoon") {
    displayTxt = "Install Soon";
  } else if (found.type == "installASAP") {
    displayTxt = "Install ASAP";
  }
  return { ...found, displayTxt } || false;
}

async function patchCancelCommand(cmd) {
  patchCommandLoading.value = true;
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "cancelCommand",
      licenseId: props.licenseId,
      serial: device.value.serial,
      id: cmd._id,
    });
    console.log(result);
    getData(props.device); // Refresh device data
  } catch (error) {
    console.error("Error cancelling patch command", error);
  }
  patchCommandLoading.value = false;
}

async function handleCreatePatchCommand(patch, commandType) {
  console.log("Creating patch command for:", patch, "Type:", commandType);
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "addDeviceCommand",
      licenseId: props.licenseId,
      serial: device.value.serial,
      type: commandType,
      appId: patch.appID,
      version: patch.version,
      status: "pending",
    });
    console.log("Patch command created:", result);
    getData(props.device); // Refresh device data
  } catch (error) {
    console.error("Error creating patch command:", error);
  }
}
</script>

<template>
  <v-overlay v-model="patchActionOverlay" class="align-center justify-center">
    <v-card maxWidth="500">
      <v-card-title>Patch Available</v-card-title>
      <v-card-text>
        <v-table>
          <tbody>
            <tr v-for="detail in patchActionDetail" :key="detail.title">
              <td class="font-weight-bold">{{ detail.title }}</td>
              <td>{{ detail.value }}</td>
            </tr>
            <tr>
              <td colspan="2">
                <p class="mt-3">
                  If this individual patch needs to be installed urgently a
                  command can be added to install the update faster using one of
                  the following options
                </p>
                <p>
                  <span class="font-weight-bold">Install Soon</span> - Will
                  reduce the Update Deadline to zero for this patch
                </p>
                <p>
                  <span class="font-weight-bold">Instal ASAP</span> - Will
                  reduce the Update Deadline to zero and the Grace period to
                  zero. This is the fastest way to deploy the patch.
                </p>
                <v-select
                  class="mt-3"
                  :items="patchActionTypes"
                  v-model="selectedPatch.type"
                  label="Installation Option"
                  variant="outlined"
                ></v-select>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="patchActionSaveButton" :loading="patchCommandLoading"
          >Apply Command</v-btn
        >
        <v-btn @click="patchActionOverlay = false">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-overlay>

  <v-dialog v-model="deleteDeviceDialog" max-width="500">
    <v-card rounded="lg">
      <v-card-text>
        <h1>Delete Device</h1>
        You are about to <strong>DELETE</strong> this device and its history.
        Please click <strong>DELETE</strong> below if you would like to proceed
        or <strong>CANCEL</strong> to back out.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          variant="elevated"
          color="primary"
          @click="deleteDeviceConfirmed"
          :loading="deleteDeviceLoading"
        >
          Delete
        </v-btn>
        <v-btn
          variant="elevated"
          @click="deleteDeviceDialog = false"
          :disabled="deleteDeviceLoading"
        >
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-row class="pt-3">
    <v-col class="pb-0 mb-0 mx-1">
      <h2>{{ props.device.computerName }}</h2>
    </v-col>
    <v-spacer></v-spacer>
  </v-row>
  <v-row>
    <v-col class="pt-0 mt-0 mb-3 mx-1">
      <v-tabs v-model="selectedTab" color="#e7541e" :loading="loading">
        <v-tab value="Overview">Overview</v-tab>
        <v-tab value="Patching" :disabled="loading"
          >Patching
          {{
            props.device.updatesAvailableCount
              ? "(" + props.device.updatesAvailableCount + " Updates Available)"
              : ""
          }}
        </v-tab>
        <v-tab value="Inventory" :disabled="loading"
          >Application Inventory</v-tab
        >
      </v-tabs>
    </v-col>
    <v-col cols="auto" v-if="!disableDelete">
      <v-btn
        variant="elevated"
        color="primary"
        class="ml-3"
        @click="deleteDeviceDialog = true"
      >
        Delete
      </v-btn>
    </v-col>
  </v-row>

  <v-window v-model="selectedTab">
    <v-window-item value="Inventory">
      <InventoryAppsList
        v-if="device.inventory"
        :inventory="device.inventory.inventory"
      />
    </v-window-item>

    <v-window-item value="Overview">
      <v-sheet rounded="lg" elevation="1" height="100%" class="pa-5 ma-1">
        <v-row>
          <v-spacer></v-spacer>
          <v-col
            cols="auto"
            v-for="item in topIconRow"
            :key="item.title"
            class="d-flex"
          >
            <v-tooltip activator="parent" location="bottom">{{
              item.disabled ? item.disabledTooptip : item.tooltip
            }}</v-tooltip>
            <v-sheet
              :height="50"
              :width="50"
              class="d-flex justify-center align-center pa-0 ma-0"
              :color="item.disabled ? 'grey-lighten-1' : '#e7541e'"
              rounded
            >
              <v-icon :opacity="item.disabled ? 0.2 : 1">{{
                item.icon
              }}</v-icon>
            </v-sheet>
            <v-sheet class="d-flex flex-column pl-5">
              <v-sheet
                :class="{
                  'font-weight-medium': true,
                  'text-disabled': item.disabled,
                }"
                >{{ item.title }}</v-sheet
              >
              <v-sheet
                :class="{
                  'font-weight-bold': true,
                  'text-disabled': item.disabled,
                }"
                >{{ item.value }}</v-sheet
              >
            </v-sheet>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </v-sheet>
      <v-row>
        <v-col cols="6">
          <BasicTable :data="general" />
        </v-col>
        <v-col cols="6">
          <BasicTable :data="hardware" />
        </v-col>
      </v-row>
    </v-window-item>
    <v-window-item value="Patching">
      <PatchesAvailable
        :updates="device.updatesAvailable || []"
        :loading="loading"
        :commands="patchCommands"
        :serialNumber="device.serial"
        @cancel-command="patchCancelCommand"
        @update-commands="getData(props.device)"
      />

      <v-row>
        <v-col>
          <PatchLogs
            :serialNumber="
              props.device.Serial_Number
                ? props.device.Serial_Number
                : props.device.serial
            "
            :licenseId="props.licenseId"
          />
        </v-col>
      </v-row>
    </v-window-item>
  </v-window>
</template>
